import { moneyParseByDecimal } from "@sembark-travel/money"
import {
	TCustomerPaymentPreference,
	TCustomerPaymentPreferencePayload,
} from "./store"
import type { TPaymentRuleFormValues } from "./NewItem"

export type TInstalmentBreakdown = TPaymentRuleFormValues["breakdowns"][number]

export function ruleFormValuesToPayload(
	values: TPaymentRuleFormValues
): TCustomerPaymentPreferencePayload {
	const isSpecific = values.condition_type === "specific"
	const daysBeforeTripStart =
		isSpecific && values.days_before_trip_start.is_include
			? {
					condition: values.days_before_trip_start.condition,
					days: Number(values.days_before_trip_start.days),
				}
			: undefined
	const tripDuration =
		isSpecific && values.trip_duration.is_include
			? {
					condition: values.trip_duration.condition,
					nights: Number(values.trip_duration.nights),
				}
			: undefined
	const minFirstInstalmentAmount = values.min_first_instalment_amount.is_include
		? values.min_first_instalment_amount.values.map((v) => ({
				amount: Number(v.amount),
				currency: v.currency,
			}))
		: undefined

	return {
		is_fallback_rule: isSpecific ? 0 : 1,
		...(daysBeforeTripStart
			? { days_before_trip_start: daysBeforeTripStart }
			: {}),
		...(tripDuration ? { trip_duration: tripDuration } : {}),
		...(minFirstInstalmentAmount
			? { min_first_instalment_amount: minFirstInstalmentAmount }
			: {}),
		breakdowns: values.breakdowns.map((b) => ({
			reference: Number(b.reference),
			day_offset: Number(b.day_offset),
			instalment_breakup_in_percent: Number(b.instalment_breakup_in_percent),
		})),
		is_include_flight_cost_in_first_instalment: 0,
	}
}

export function getTotalInstalmentShare(
	breakdowns?: Array<
		Pick<TInstalmentBreakdown, "instalment_breakup_in_percent">
	>
): number {
	const raw = (breakdowns || []).reduce((total, b) => {
		const val = Number(b?.instalment_breakup_in_percent || 0)
		return total + (isNaN(val) ? 0 : val)
	}, 0)
	return Math.round(raw * 100) / 100
}

// A rule without any condition is the fallback rule, applied to every booking
// that no other rule matches. The API sends the human readable conditions in
// `preference_condition`, so an empty one is enough to tell that it is the
// fallback rule.
export function isFallbackRule(rule: TCustomerPaymentPreference): boolean {
	if (rule.days_before_trip_start || rule.trip_duration) return false
	return !rule.preference_condition?.trim()
}

export function formatRuleConditionLabel(
	rule: TCustomerPaymentPreference
): string {
	const condition = rule.preference_condition?.trim()
	return condition || "Fallback Rule"
}

// `day_offset` is signed: negative is before the reference event, positive is
// after it and 0 is on the day of the event itself.
export function formatDayOffsetLabel(dayOffset?: number): string {
	const offset = Number(dayOffset || 0)
	if (!offset || isNaN(offset)) return "on"
	const days = Math.abs(offset)
	return `${days} ${days === 1 ? "day" : "days"} ${offset < 0 ? "before" : "after"}`
}

export function formatInstalmentLabel({
	share,
	dayOffset,
	referenceName,
}: {
	share?: number
	dayOffset?: number
	referenceName?: string
}): string | null {
	if (!referenceName || share === undefined || share === null || isNaN(share))
		return null
	return `${share}% ${formatDayOffsetLabel(dayOffset)} ${referenceName}`
}

// The saved rules come with the name of their reference event in
// `reference_label`. Instalments without one are skipped.
export function formatRuleInstalmentLabels(
	rule: Pick<TCustomerPaymentPreference, "breakdowns">
): Array<string> {
	return (rule.breakdowns || [])
		.map((b) =>
			formatInstalmentLabel({
				share: Number(b.instalment_breakup_in_percent),
				dayOffset: b.day_offset,
				referenceName: b.reference_label,
			})
		)
		.filter((label): label is string => Boolean(label))
}

// The form only holds the reference id, so the names come from the references
// listing. Instalments whose name isn't known yet (e.g. while the listing is
// loading) are skipped.
export function formatFormInstalmentLabels(
	breakdowns: Array<TInstalmentBreakdown>,
	referenceNames?: Record<number, string>
): Array<string> {
	return (breakdowns || [])
		.map((b) =>
			formatInstalmentLabel({
				share: Number(b.instalment_breakup_in_percent),
				dayOffset: b.day_offset,
				referenceName: getReferenceNameById(b.reference, referenceNames),
			})
		)
		.filter((label): label is string => Boolean(label))
}

function getReferenceNameById(
	reference: TPaymentRuleFormValues["breakdowns"][number]["reference"],
	referenceNames?: Record<number, string>
): string | undefined {
	if (reference === undefined) return undefined
	const id = Number(reference)
	if (isNaN(id)) return undefined
	return referenceNames?.[id]
}

export function formatFormMinFirstInstalmentAmounts(
	minAmounts?: TPaymentRuleFormValues["min_first_instalment_amount"]
): TCustomerPaymentPreference["min_first_instalment_amounts"] {
	if (!minAmounts?.is_include || !Array.isArray(minAmounts.values)) {
		return []
	}
	return minAmounts.values
		.filter((v) => {
			const amount = Number(v.amount)
			return !isNaN(amount) && amount > 0
		})
		.map((v, index) => {
			const parsedAmount = Number(v.amount)
			const currency = v.currency
			const money = moneyParseByDecimal(parsedAmount, currency)
			return {
				id: index,
				currency,
				amount: {
					amount: money.amount,
					currency,
					formatted: String(parsedAmount),
				},
			}
		})
}

// Reference events and their corresponding bar colors:
// 0: Booking -> #3A5BC7
// 1: Check-in -> #9E018C
// 2: Check-out -> #00749E
// 3: Booking Month End -> #3D009E
// 4: Checkout Month End -> #8200DB
const REFERENCE_EVENT_COLORS: Record<number, string> = {
	0: "#3A5BC7",
	1: "#9E008C",
	2: "#00749E",
	3: "#3D009E",
	4: "#8200DB",
}

function getReferenceEventColor(reference?: number | string): string {
	if (reference !== undefined && reference !== "") {
		const parsed = Number(reference)
		if (!isNaN(parsed) && parsed in REFERENCE_EVENT_COLORS) {
			return REFERENCE_EVENT_COLORS[parsed]
		}
	}

	return "gray"
}

const CHECKOUT_MONTH_END_REFERENCE_ID = 4

export function shouldShowInstalmentArrow({
	dayOffset,
	referenceId,
}: {
	dayOffset?: number
	referenceId?: number
}): boolean {
	if (dayOffset !== undefined && dayOffset !== null && Number(dayOffset) > 0) {
		return true
	}

	if (referenceId === CHECKOUT_MONTH_END_REFERENCE_ID) {
		return true
	}

	return false
}

export type TShareSegment = {
	share: number
	color: string
	referenceId?: number
	referenceName?: string
	dayOffset?: number
	showArrow?: boolean
}

export function getShareSegments(
	breakdowns?: Array<{
		instalment_breakup_in_percent?: number
		reference?: number | string
		reference_label?: string
		day_offset?: number
	}>,
	referenceNames?: Record<number, string>
): Array<TShareSegment> {
	return (breakdowns || [])
		.map((b) => {
			let referenceId: number | undefined
			let referenceName: string | undefined =
				b.reference_label?.trim() || undefined
			if (b.reference !== undefined && b.reference !== "") {
				const id = Number(b.reference)
				if (!isNaN(id)) referenceId = id
			}
			if (!referenceName && referenceId !== undefined) {
				referenceName = referenceNames?.[referenceId]
			}

			return {
				share: Number(b.instalment_breakup_in_percent || 0),
				color: getReferenceEventColor(b.reference),
				referenceId,
				referenceName,
				dayOffset: b.day_offset,
				showArrow: shouldShowInstalmentArrow({
					dayOffset: b.day_offset,
					referenceId,
				}),
			}
		})
		.filter((s) => !isNaN(s.share) && s.share > 0)
}
